import React, { FC, useEffect, useState } from 'react';
import { SortSelector } from '@/components/SortSelector';
import { DamsMediaResourceType, MediaResourceType } from '@/declarations/models/MediaResourceType';
import { ViewType } from '@/declarations/ViewType';
import { GenericMedia } from '@/declarations/GenericMedia';
import { Api } from '@/services/Api';
import { toggleItem } from '@/utils/array';
import { SortDirection, SortOption, SortType } from '@/declarations/models/SortOption';
import { DamsMediaItem } from '@/declarations/models/DamsMediaResponse';
import { mapTitleToMediaLicense } from '@/utils/mapTitleToMediaLicense';
import { Settings } from '@/Settings';
import {
  DamsDocumentTypeToMediaResourceType,
  MediaResourceTypeToDamsMediaResourceType,
} from '@/declarations/enums/MediaTypeMap';
import MediaList from '../../MediaList/MediaList';
import { useStore } from '../../store/Store';
import FinderLayout from '../FinderLayout';
import SelectedDamsItem from './SelectedDamsItem';
import DamsFinderHeader from './DamsFinderHeader';
import Layout from '../../Layout';

export interface DamsFinderProps {
  open: boolean;
  onClose: () => void;
  onSelectionConfirmed: (selectedItems: Array<GenericMedia<DamsMediaItem>>) => void;
  defaultResourceType: MediaResourceType;
  selectableResourceTypes: Array<MediaResourceType>;
  multiSelect?: boolean;
}

const defaultSortOption: SortOption = {
  sortBy: SortType.UPDATED_AT,
  order: SortDirection.DESC,
};

export const DamsMediaFinder: FC<DamsFinderProps> = ({
  open,
  onClose,
  onSelectionConfirmed,
  defaultResourceType,
  selectableResourceTypes,
  multiSelect = true,
}) => {
  const store = useStore();
  const site = store?.state?.selectedSite;
  const siteData = store?.state?.selectedSiteData;
  const siteId = site?.id || 0;
  const damsEmsDomain = Settings.viteEnvironmentIsProduction ? 'ems.dimu.org' : 'ems-test.dimu.org';

  const [selectedMediaResourceType, setSelectedMediaResourceType] = useState<
    MediaResourceType | MediaResourceType[] | ''
  >(defaultResourceType);

  const [mediaItems, setMediaItems] = useState<Array<GenericMedia<DamsMediaItem>>>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<{ start: number; rows: number }>({ start: 0, rows: 20 });
  const [sortOption, setSortOption] = useState<SortOption>(defaultSortOption);
  const [searchQuery, _setSearchQuery] = useState<string>('');

  // default selectedSiteData sin owner.ekultur_museum_id hvis den finnes, ellers null.
  const [ekulturMuseumId, setEkulturMuseumId] = useState<number | null>(siteData?.owner?.ekultur_museum_id || null);

  const setSearchQuery = (query: string) => {
    setPage((prevPage) => ({ ...prevPage, start: 0 }));
    _setSearchQuery(query);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<Array<GenericMedia<DamsMediaItem>>>([]);

  const _onClose = () => {
    onClose();
    setMediaItems([]);
    setSelectedItems([]);
    setSelectedMediaResourceType(defaultResourceType);
    setSearchQuery('');
    setSortOption(defaultSortOption);
    setTotalCount(0);
    setPage({ start: 0, rows: 20 });
    setEkulturMuseumId(siteData?.owner?.ekultur_museum_id || null);
  };

  const isSelected = (item: GenericMedia<DamsMediaItem>): boolean => {
    return selectedItems.some((i) => i.id === item.id);
  };

  const handleSelectionChanged = (item: GenericMedia<DamsMediaItem>) => {
    if (multiSelect) {
      setSelectedItems((selected) => toggleItem(selected, item, (a, b) => a.id === b.id));
    } else {
      setSelectedItems((previouslySelected) =>
        previouslySelected.length === 1 && previouslySelected[0].id === item.id ? [] : [item],
      );
    }
  };

  const handlePageChanged = (changes: Partial<typeof page>) => {
    setPage((prevPage) => ({ ...prevPage, ...changes }));
  };

  const handleSelectionConfirmed: DamsFinderProps['onSelectionConfirmed'] = (selected) => {
    _onClose();
    setMediaItems([]);
    setSelectedItems([]);
    onSelectionConfirmed(selected);
  };

  // When finder opens, reset ekulturMuseumId to site default
  useEffect(() => {
    if (open) {
      setEkulturMuseumId(siteData?.owner?.ekultur_museum_id || null);
      setPage((prev) => ({ ...prev, start: 0 }));
    }
  }, [open, siteData]);

  useEffect(() => {
    if (!open) {
      return;
    }

    // Guard: do not fetch until a museum is selected
    if (ekulturMuseumId == null) {
      setIsLoading(false);
      setMediaItems([]);
      setTotalCount(0);
      return;
    }

    let unmounted = false;
    setIsLoading(true);

    let documentType: string[] | undefined;
    if (selectedMediaResourceType === '') {
      documentType = selectableResourceTypes
        .map((type) => MediaResourceTypeToDamsMediaResourceType[type])
        .filter((v): v is DamsMediaResourceType => v !== undefined)
        .map(String);
    } else if (Array.isArray(selectedMediaResourceType)) {
      documentType = selectedMediaResourceType
        .map((type) => MediaResourceTypeToDamsMediaResourceType[type])
        .filter((v): v is DamsMediaResourceType => v !== undefined)
        .map(String);
    } else {
      const mapped = MediaResourceTypeToDamsMediaResourceType[selectedMediaResourceType];
      documentType = mapped !== undefined ? [String(mapped)] : undefined;
    }

    const ctx = Api.getDAMSObjects({
      q: searchQuery ? `*${searchQuery}*` : '*',
      museum_ids: ekulturMuseumId.toString(),
      start: page.start,
      rows: page.rows,
      sort: `${sortOption.sortBy} ${sortOption.order}`,
      document_type: documentType,
      sync_tag: { name: 'M24', state: 'success' },
    });

    ctx
      .fetchDirect(null)
      .then((result) => {
        if (!unmounted) {
          setTotalCount(result?.count || 0);
          setMediaItems(
            (result?.objects || []).map((item) => ({
              id: String((item as DamsMediaItem).unique_id ?? ''),
              title: (item as DamsMediaItem).title ?? '',
              filename: (item as DamsMediaItem).title ?? '',
              license: mapTitleToMediaLicense((item as DamsMediaItem).licenses?.[0]?.title),
              url: `https://${damsEmsDomain}/image/${(item as DamsMediaItem).m24?.ems_id ?? ''}`,
              mimetype: '',
              source: {
                ...(item as DamsMediaItem),
                dms_environment: Settings.viteEnvironmentIsProduction ? 'production' : 'test',
                type: DamsDocumentTypeToMediaResourceType[(item as DamsMediaItem).document_type],
              },
              description: (item as DamsMediaItem).description ?? '',
              modelType: 'dams',
            })),
          );
        }
      })
      .finally(() => {
        if (!unmounted) {
          setIsLoading(false);
        }
      });

    return () => {
      unmounted = true;
      ctx.abort();
    };
  }, [
    page,
    selectedMediaResourceType,
    siteId,
    open,
    searchQuery,
    sortOption,
    damsEmsDomain,
    ekulturMuseumId,
    selectableResourceTypes,
  ]);

  const handleEkulturMuseumIdChange = React.useCallback((id: number | null) => {
    setEkulturMuseumId(id);
    setPage((prevPage) => ({ ...prevPage, start: 0 }));
  }, []);
  return (
    <FinderLayout<DamsMediaItem>
      open={open}
      selectedItems={selectedItems}
      renderSelectedItem={(item) => <SelectedDamsItem item={item} onRemoveItem={handleSelectionChanged} />}
      onConfirmSelection={handleSelectionConfirmed}
      onClose={_onClose}>
      <Layout
        headerContent={
          <DamsFinderHeader
            resourceType={selectedMediaResourceType}
            onResourceTypeChanged={setSelectedMediaResourceType}
            searchQuery={searchQuery}
            onQueryChange={setSearchQuery}
            selectableResourceTypes={selectableResourceTypes}
            ekulturMuseumId={ekulturMuseumId}
            onSetEkulturMuseumId={handleEkulturMuseumIdChange}
          />
        }>
        <MediaList
          isLoading={isLoading}
          items={mediaItems}
          page={page}
          totalItemCount={totalCount}
          onPageChange={(c, start, rows) => handlePageChanged({ start, rows })}
          onPageSizeChange={(rows) => handlePageChanged({ rows })}
          defaultSelectedViewType={ViewType.GRID}
          getIsSelected={isSelected}
          onSelectChanged={handleSelectionChanged}
          isSelectable
          isFavouriteable={false}
          customHeaderElement={
            <SortSelector
              availableOptions={[SortType.UPDATED_AT, SortType.CREATED_AT]}
              sortOption={sortOption}
              setSortOption={setSortOption}
            />
          }
          gridThumbnailWidth={200}
        />
      </Layout>
    </FinderLayout>
  );
};

export default DamsMediaFinder;
